import React, { useRef, useState, useEffect } from "react";
import { Input } from "antd";
import { toast } from "react-toastify";
import { CustomInput, ErrorText } from "./ui";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../slices/authSlice";
import { saveToLocalStorage } from "../utils/localStorage";
import { setIsLoggedIn, setRole } from "../slices/app";
import { useNavigate } from "react-router-dom";
import { validateFormHandler } from "../utils/validations/validateForm";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8011";

const ForgotPassword = ({ onBackToSignin }) => {
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [timer, setTimer] = useState(300);
  const inputRefs = useRef([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Format mm:ss
  const formatTime = (time) => {
    const m = Math.floor(time / 60);
    const s = time % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  useEffect(() => {
    if (timer > 0 && otpSent) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer, otpSent]);

  const handleChange = (value, index) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();

    const validationErrors = validateFormHandler({
      email,
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/send-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });
      if (res.ok) {
        toast.success("Reset OTP has been sent.");
        setOtpSent(true);
        setTimer(300);
      } else {
        const err = await res.json();
        toast.error(err.error || "Failed to send OTP");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error sending OTP");
    } finally {
      setIsLoading(false);
    }
  };
  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");
    if (!otpValue || otpValue.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/verify-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase(), otp: otpValue }),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("OTP verified successfully");
        setIsOtpVerified(true); // ✅ Enable password fields
      } else {
        toast.error(data.error || "Invalid or expired OTP");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error verifying OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");

    const validationErrors = validateFormHandler({
      email,
      otp: otpValue,
      newPassword,
      confirmPassword,
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.toLowerCase(),
          otp: otpValue,
          newPassword,
        }),
      });
      if (res.ok) {
        toast.success("Password reset successful. Please sign in.");
        onBackToSignin?.();
      } else {
        const err = await res.json();
        toast.error(err.error || "Failed to reset password");
      }
    } catch (err) {
      console.error("reset error", err);
      toast.error("An error occurred while resetting password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/send-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });
      if (res.ok) {
        toast.success("OTP resent (if email is registered).");
        setOtpSent(true);
        setTimer(300);
      } else {
        const err = await res.json();
        toast.error(err.error || "Failed to resend OTP");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error resending OTP");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (otpSent && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [otpSent]);

  return (
    <div className="h-full flex items-center justify-center">
      <div className="max-w-md w-full bg-white py-6 px-8 rounded-2xl border-4 border-gray-200">
        <h2 className="text-2xl font-bold text-center text-themeColor mb-3">
          Reset Password
        </h2>

        {!otpSent ? (
          // Email input step
          <form onSubmit={handleSendOtp}>
            <div className="mb-4">
              <CustomInput
                type="email"
                label="Email"
                value={email}
                placeholder="Enter registered email"
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email)
                    setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                message={errors.email}
              />
            </div>

            <button
              type="submit"
              className={`w-full py-2 px-4 bg-themeColor text-white font-semibold rounded-md shadow-md ${
                isLoading ? "cursor-not-allowed opacity-50" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? "Sending OTP..." : "Send OTP"}
            </button>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => onBackToSignin?.()}
                className="text-themeColor font-semibold"
              >
                Back to Sign in
              </button>
            </div>
          </form>
        ) : (
          // OTP + password step
          <form onSubmit={handleReset}>
            {!isOtpVerified && (
              <>
                <p className="text-gray-600 text-center mb-4">
                  Enter the 6-digit OTP sent to{" "}
                  <span className="font-semibold">{email}</span>
                </p>

                {/* OTP Boxes */}
                <div className="flex flex-col justify-center mb-5">
                  <div className="flex justify-center gap-3">
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputRefs.current[i] = el)}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleChange(e.target.value, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-themeColor"
                      />
                    ))}
                  </div>
                  <span className="self-center px-3 font-semibold text-[#dc2626] mt-2">
                    {timer > 0 ? `${formatTime(timer)}s` : ""}
                  </span>
                </div>
                <div className="flex justify-center mb-4">
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={isOtpVerified || isLoading}
                    className={`py-2 px-4 rounded-md font-semibold text-white ${
                      isOtpVerified
                        ? "bg-green-500 cursor-not-allowed"
                        : "bg-themeColor hover:bg-themeColor/80"
                    }`}
                  >
                    {isOtpVerified ? "OTP Verified" : "Verify OTP"}
                  </button>
                </div>
                {isOtpVerified && (
                  <p className="text-green-600 text-center font-medium mt-2">
                    OTP Verified!
                  </p>
                )}
              </>
            )}

            {/* Password Inputs */}
            {/* Password Inputs */}
            {isOtpVerified && (
              <>
                {" "}
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">
                    New Password
                  </label>
                  <Input.Password
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (errors.newPassword) {
                        setErrors((prev) => ({
                          ...prev,
                          newPassword: undefined,
                        }));
                      }
                    }}
                    placeholder="Enter new password"
                    className="w-full"
                    disabled={!isOtpVerified}
                  />
                  {errors.newPassword && (
                    <ErrorText message={errors.newPassword} />
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <Input.Password
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors((prev) => ({
                          ...prev,
                          confirmPassword: undefined,
                        }));
                      }
                    }}
                    placeholder="Confirm new password"
                    className="w-full"
                    disabled={!isOtpVerified}
                  />
                  {errors.confirmPassword && (
                    <ErrorText message={errors.confirmPassword} />
                  )}
                </div>
                <button
                  type="submit"
                  className={`w-full py-2 px-4 bg-themeGradient text-white font-semibold rounded-md ${
                    isLoading ? "cursor-not-allowed opacity-50" : ""
                  }`}
                  disabled={isLoading || !isOtpVerified}
                >
                  {isLoading ? "Resetting..." : "Reset Password"}
                </button>
              </>
            )}

            {!isOtpVerified && (
              <div className="mt-4 text-center">
                <span className="text-gray-600">Didn’t receive the code?</span>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={timer > 0}
                  className={`ml-1 font-semibold transition duration-200 ${
                    timer > 0
                      ? "text-gray-400 cursor-not-allowed opacity-50"
                      : "text-themeColor"
                  }`}
                >
                  Resend OTP
                </button>
              </div>
            )}

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => onBackToSignin?.()}
                className="text-themeColor font-semibold"
              >
                Back to Sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
